import React, { useMemo } from 'react';
import { Box, Typography, Paper, ToggleButton, ToggleButtonGroup, Card, CardContent, Grid } from '@mui/material';
import type { ProgramExercise } from '../services/types';
import ExerciseMuscleMap from './ExerciseMuscleMap';

interface WorkoutSet {
  reps: number;
  weight: number;
  rpe: number | null;
  assistance: boolean | null;
}

interface WorkoutExercise {
  exerciseId: number;
  exerciseName: string;
  sets: WorkoutSet[];
  muscleGroup?: string;
  primaryMuscleGroup?: string;
  secondaryMuscleGroup?: string;
  tertiaryMuscleGroup?: string;
}

interface WorkoutSession {
  id: number;
  name: string;
  date: string;
  programDayNumber: number | null;
  exercises: WorkoutExercise[];
}

interface MuscleLoadVisualizationProps {
  programDays: { [day: number]: ProgramExercise[] };
  selectedDay?: number | null; // null means show all days
  onDayChange?: (day: number | null) => void;
  workoutSessions?: WorkoutSession[]; // Add workout sessions for weight calculation
  exerciseOptions?: Array<{ id: number; muscleGroup: string; primaryMuscleGroup?: string; secondaryMuscleGroup?: string; tertiaryMuscleGroup?: string; }>;
  valueMode?: 'load' | 'weight'; // What values to show in body diagram
}

interface MuscleLoad {
  [muscleGroup: string]: number;
}

// Muscle group details with colors and labels (muted, low-contrast palette)
const muscleGroupInfo: Record<string, { label: string; color: string; colorLight: string; }> = {
  'chest': { label: 'Гръдни', color: '#B07070', colorLight: '#3A2828' },
  'back': { label: 'Гръб', color: '#6B9E99', colorLight: '#263635' },
  'quadriceps': { label: 'Квадрицепс', color: '#6A9AAA', colorLight: '#26353A' },
  'hamstrings': { label: 'Задно бедро', color: '#5A8A9A', colorLight: '#22313A' },
  'adductors': { label: 'Аддуктори', color: '#7AAAB8', colorLight: '#28383E' },
  'shoulders': { label: 'Рамене', color: '#B08868', colorLight: '#3A2E22' },
  'arms': { label: 'Ръце', color: '#7DA898', colorLight: '#2A3632' },
  'core': { label: 'Корем', color: '#B0A060', colorLight: '#3A3622' },
  'glutes': { label: 'Глутеуси', color: '#A07898', colorLight: '#362A34' },
  'calves': { label: 'Икри', color: '#7AAA8E', colorLight: '#283830' },
  'cardio': { label: 'Кардио', color: '#B09850', colorLight: '#3A3220' },
};


const MuscleLoadVisualization: React.FC<MuscleLoadVisualizationProps> = ({
  programDays,
  selectedDay,
  onDayChange,
  workoutSessions = [],
  exerciseOptions = [],
  valueMode = 'load',
}) => {
  const [internalValueMode, setInternalValueMode] = React.useState<'load' | 'weight'>(valueMode);

  // Update internal state when prop changes
  React.useEffect(() => {
    setInternalValueMode(valueMode);
  }, [valueMode]);
  // Calculate muscle load for selected day or all days
  const { muscleLoad, muscleWeightLifted } = useMemo(() => {
    const load: MuscleLoad = {};
    const weightLifted: { [muscleGroup: string]: number } = {};

    const addMuscleLoad = (muscle: string | undefined, weight: number) => {
      if (muscle && muscle !== 'cardio') {
        load[muscle] = (load[muscle] || 0) + weight;
      }
    };

    const addMuscleWeight = (muscle: string | undefined, totalWeight: number, multiplier: number = 1.0) => {
      if (muscle && muscle !== 'cardio') {
        weightLifted[muscle] = (weightLifted[muscle] || 0) + (totalWeight * multiplier);
      }
    };

    if (selectedDay !== null && selectedDay !== undefined) {
      // Load for specific day
      const exercises = programDays[selectedDay] || [];
      exercises.forEach(pe => {
        if (pe.exercise.primaryMuscleGroup) {
          addMuscleLoad(pe.exercise.primaryMuscleGroup, 1.0);
        } else {
          addMuscleLoad(pe.exercise.muscleGroup, 1.0);
        }

        if (pe.exercise.secondaryMuscleGroup) {
          addMuscleLoad(pe.exercise.secondaryMuscleGroup, 0.5);
        }

        if (pe.exercise.tertiaryMuscleGroup) {
          addMuscleLoad(pe.exercise.tertiaryMuscleGroup, 0.25);
        }

        if (pe.exercise.muscleGroup === 'cardio') {
          load['cardio'] = (load['cardio'] || 0) + 1;
        }
      });

      // Calculate weight lifted from the last workout for this day
      const dayWorkouts = workoutSessions.filter(ws => ws.programDayNumber === selectedDay);
      if (dayWorkouts.length > 0) {
        // Get the most recent workout (last in array, assuming sorted by date)
        const lastWorkout = dayWorkouts[dayWorkouts.length - 1];

        lastWorkout.exercises.forEach(ex => {
          // Calculate total volume for this exercise (sets × reps × weight)
          const totalVolume = ex.sets.reduce((sum, set) => sum + (set.reps * set.weight), 0);

          // Use muscle groups from exercise object if available, otherwise lookup in exerciseOptions
          const primaryMuscle = ex.primaryMuscleGroup || ex.muscleGroup;
          const secondaryMuscle = ex.secondaryMuscleGroup;
          const tertiaryMuscle = ex.tertiaryMuscleGroup;

          // Fallback to exerciseOptions if not in exercise object
          const exerciseInfo = (!primaryMuscle && !secondaryMuscle && !tertiaryMuscle)
            ? exerciseOptions.find(opt => opt.id === ex.exerciseId)
            : null;

          if (primaryMuscle || exerciseInfo) {
            const muscle = primaryMuscle || exerciseInfo?.primaryMuscleGroup || exerciseInfo?.muscleGroup;
            addMuscleWeight(muscle, totalVolume, 1.0);

            const secondary = secondaryMuscle || exerciseInfo?.secondaryMuscleGroup;
            if (secondary) {
              addMuscleWeight(secondary, totalVolume, 0.5);
            }

            const tertiary = tertiaryMuscle || exerciseInfo?.tertiaryMuscleGroup;
            if (tertiary) {
              addMuscleWeight(tertiary, totalVolume, 0.25);
            }
          }
        });
      }
    } else {
      // Load for all days
      Object.values(programDays).forEach(exercises => {
        exercises.forEach(pe => {
          if (pe.exercise.primaryMuscleGroup) {
            addMuscleLoad(pe.exercise.primaryMuscleGroup, 1.0);
          } else {
            addMuscleLoad(pe.exercise.muscleGroup, 1.0);
          }

          if (pe.exercise.secondaryMuscleGroup) {
            addMuscleLoad(pe.exercise.secondaryMuscleGroup, 0.5);
          }

          if (pe.exercise.tertiaryMuscleGroup) {
            addMuscleLoad(pe.exercise.tertiaryMuscleGroup, 0.25);
          }

          if (pe.exercise.muscleGroup === 'cardio') {
            load['cardio'] = (load['cardio'] || 0) + 1;
          }
        });
      });

      // Calculate weight lifted from all workouts (most recent for each day)
      const workoutsByDay = new Map<number, WorkoutSession>();

      workoutSessions.forEach(ws => {
        if (ws.programDayNumber !== null) {
          const existing = workoutsByDay.get(ws.programDayNumber);
          // Keep the most recent workout (assuming later in array = more recent)
          if (!existing || new Date(ws.date) > new Date(existing.date)) {
            workoutsByDay.set(ws.programDayNumber, ws);
          }
        }
      });

      workoutsByDay.forEach(workout => {
        workout.exercises.forEach(ex => {
          const totalVolume = ex.sets.reduce((sum, set) => sum + (set.reps * set.weight), 0);

          // Use muscle groups from exercise object if available, otherwise lookup in exerciseOptions
          const primaryMuscle = ex.primaryMuscleGroup || ex.muscleGroup;
          const secondaryMuscle = ex.secondaryMuscleGroup;
          const tertiaryMuscle = ex.tertiaryMuscleGroup;

          // Fallback to exerciseOptions if not in exercise object
          const exerciseInfo = (!primaryMuscle && !secondaryMuscle && !tertiaryMuscle)
            ? exerciseOptions.find(opt => opt.id === ex.exerciseId)
            : null;

          if (primaryMuscle || exerciseInfo) {
            const muscle = primaryMuscle || exerciseInfo?.primaryMuscleGroup || exerciseInfo?.muscleGroup;
            addMuscleWeight(muscle, totalVolume, 1.0);

            const secondary = secondaryMuscle || exerciseInfo?.secondaryMuscleGroup;
            if (secondary) {
              addMuscleWeight(secondary, totalVolume, 0.5);
            }

            const tertiary = tertiaryMuscle || exerciseInfo?.tertiaryMuscleGroup;
            if (tertiary) {
              addMuscleWeight(tertiary, totalVolume, 0.25);
            }
          }
        });
      });
    }

    return { muscleLoad: load, muscleWeightLifted: weightLifted };
  }, [programDays, selectedDay, workoutSessions, exerciseOptions]);

  // Get opacity based on load intensity
  const getOpacity = (load: number): number => {
    if (load === 0) return 0.15;
    if (load <= 0.5) return 0.3;
    if (load <= 1) return 0.5;
    if (load <= 2) return 0.7;
    return 1;
  };

  const getLoadLevel = (load: number): string => {
    if (load === 0) return 'Няма';
    if (load <= 0.5) return 'Лека';
    if (load <= 1) return 'Лека';
    if (load <= 2) return 'Средна';
    if (load <= 3) return 'Висока';
    return 'Много висока';
  };

  // Format load for display
  const formatLoad = (load: number): string => {
    if (load === 0) return '0';
    if (load % 1 === 0) return load.toString();
    return load.toFixed(1);
  };

  const days = Object.keys(programDays).map(Number).sort((a, b) => a - b);

  // Get all muscles in order
  const orderedMuscles = Object.keys(muscleGroupInfo).sort((a, b) => {
    const loadA = muscleLoad[a] || 0;
    const loadB = muscleLoad[b] || 0;
    return loadB - loadA; // Sort by load descending
  });


  return (
    <Paper sx={{ p: 3, background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)' }}>
      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, textAlign: 'center', color: 'text.primary' }}>
        🏋️ Натоварване на Мускулни Групи
      </Typography>

      {/* Day selector */}
      {onDayChange && days.length > 0 && (
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1 }}>
          <ToggleButtonGroup
            value={selectedDay === null ? 'all' : `day-${selectedDay}`}
            exclusive
            onChange={(_, value) => {
              if (value === 'all') {
                onDayChange(null);
              } else if (value) {
                onDayChange(parseInt(value.replace('day-', '')));
              }
            }}
            size="small"
          >
            <ToggleButton value="all" sx={{ fontWeight: 'bold' }}>Всички дни</ToggleButton>
            {days.map(day => (
              <ToggleButton key={day} value={`day-${day}`} sx={{ fontWeight: 'bold' }}>
                Ден {day}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Muscle Load Grid */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {orderedMuscles.map((muscleKey) => {
          const load = muscleLoad[muscleKey] || 0;
          const weightLifted = muscleWeightLifted[muscleKey] || 0;
          const info = muscleGroupInfo[muscleKey as keyof typeof muscleGroupInfo];
          if (!info) return null;

          const opacity = getOpacity(load);
          const maxLoad = 4;
          const percentage = Math.min((load / maxLoad) * 100, 100);

          return (
            <Grid key={muscleKey} item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  background: `linear-gradient(135deg, ${info.colorLight} 0%, rgba(30,30,30,0.9) 100%)`,
                  opacity,
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 12px rgba(0,0,0,0.3)`,
                  },
                  border: load > 0 ? `2px solid ${info.color}` : '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                      {info.label}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: info.color }}>
                      {formatLoad(load)}
                    </Typography>
                  </Box>

                  {/* Progress bar */}
                  <Box
                    sx={{
                      height: 6,
                      bgcolor: 'rgba(255,255,255,0.08)',
                      borderRadius: 3,
                      overflow: 'hidden',
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        width: `${percentage}%`,
                        bgcolor: info.color,
                        transition: 'width 0.3s ease',
                      }}
                    />
                  </Box>

                  {/* Load level label */}
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', display: 'block', fontWeight: 500 }}>
                    {getLoadLevel(load)}
                  </Typography>

                  {/* Weight lifted display */}
                  {weightLifted > 0 && (
                    <Typography variant="caption" sx={{ color: 'text.primary', fontSize: '0.75rem', fontWeight: 'bold', display: 'block', mt: 0.5 }}>
                      💪 {Math.round(weightLifted).toLocaleString('bg-BG')} kg
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Body Diagram with Muscle Values */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            Анатомия на Мускулите
          </Typography>

          {/* Toggle between load and weight */}
          {Object.keys(muscleWeightLifted).length > 0 && (
            <ToggleButtonGroup
              value={internalValueMode}
              exclusive
              onChange={(_, newMode) => {
                if (newMode) setInternalValueMode(newMode);
              }}
              size="small"
            >
              <ToggleButton value="load">Натоварване</ToggleButton>
              <ToggleButton value="weight">Тегло (kg)</ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>

        <ExerciseMuscleMap
          muscleValues={
            internalValueMode === 'weight'
              ? Object.fromEntries(
                  Object.entries(muscleWeightLifted).map(([key, value]) => [
                    key,
                    Math.round(value).toLocaleString('bg-BG')
                  ])
                )
              : muscleLoad
          }
        />
      </Box>

      {/* Legend */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5, color: 'text.primary' }}>
          📊 説明:
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={6} sm={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 20, height: 20, bgcolor: 'action.disabledBackground', borderRadius: 1 }} />
              <Typography variant="caption">Няма (0)</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 20, height: 20, bgcolor: '#6B9E99', borderRadius: 1, opacity: 0.3 }} />
              <Typography variant="caption">Лека (0.5)</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 20, height: 20, bgcolor: '#6B9E99', borderRadius: 1, opacity: 0.6 }} />
              <Typography variant="caption">Средна (2)</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 20, height: 20, bgcolor: '#B07070', borderRadius: 1, opacity: 1 }} />
              <Typography variant="caption">Висока (3+)</Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
}

export default MuscleLoadVisualization;
